import { StyledIconButton } from '@Components/UI/Analytics/Analytics.styled';
import DateRangePickerPopup, {
	DateRangeOption,
} from '@Components/UI/Pickers/DateRangePickerPopup/DateRangePickerPopup';
import ReactHookDatePicker from '@Components/UI/Pickers/ReactHookDatePicker/ReactHookDatePicker';
import { Box, Button, Icon } from '@mui/material';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

export type HubRange = { from?: Date; to?: Date };

/**
 * The hub's card-level date control: the preset popup plus a working
 * "custom" body (From/To calendars + Apply) and a Clear back to all-time —
 * the plain preset popup renders its children only in custom mode and the
 * Overview never gave it any, so custom silently did nothing.
 */
const HubDateRange: React.FC<{
	onApply: (range: HubRange, option: DateRangeOption) => void;
	allowClear?: boolean;
	/** what the page already applied on mount (e.g. the season default) */
	initialRange?: HubRange;
}> = ({ onApply, allowClear = true, initialRange }) => {
	const [rangeOption, setRangeOption] = useState<DateRangeOption>('custom');
	const [applied, setApplied] = useState<HubRange>(initialRange ?? {});
	const [open, setOpen] = useState(false);
	const [anchor, setAnchor] = useState<HTMLElement>();
	const { control, getValues, reset } = useForm<{ from: Date | null; to: Date | null }>({
		defaultValues: { from: null, to: null },
	});

	const applyPreset = (option: DateRangeOption) => {
		setRangeOption(option);
		if (option === 'custom') return; // body opens; Apply commits
		const start = new Date();
		const end = new Date();
		if (option === 'last 2 weeks') start.setDate(start.getDate() - 14);
		if (option === 'last month') start.setMonth(start.getMonth() - 1);
		if (option === 'last 3 months') start.setMonth(start.getMonth() - 2);
		setApplied({ from: start, to: end });
		onApply({ from: start, to: end }, option);
		setOpen(false);
	};

	const applyCustom = () => {
		const { from, to } = getValues();
		const range = { from: from ?? undefined, to: to ?? undefined };
		setApplied(range);
		onApply(range, 'custom');
		setOpen(false);
	};

	const clear = () => {
		reset({ from: null, to: null });
		setRangeOption('custom');
		setApplied({});
		onApply({}, 'custom');
		setOpen(false);
	};

	const shortDate = (date?: Date) =>
		date ? date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
	const label =
		rangeOption !== 'custom'
			? rangeOption
			: applied.from || applied.to
			? `${shortDate(applied.from)} - ${shortDate(applied.to) || 'now'}`
			: 'All time';

	return (
		<Box display="flex" alignItems="center" gap="0.5rem">
			<RangeLabel>{label}</RangeLabel>
			<StyledIconButton
				onClick={(event) => {
					setOpen(!open);
					setAnchor(event.currentTarget);
				}}
			>
				<Icon>event</Icon>
			</StyledIconButton>
			<DateRangePickerPopup
				open={open}
				anchorEl={anchor}
				onClose={() => setOpen(false)}
				onChange={applyPreset}
				rangeOptionValue={rangeOption}
			>
				<DateRangePickerPopup.Body>
					<PickersRow>
						<div>
							<PickerLabel>From</PickerLabel>
							<ReactHookDatePicker control={control} name="from" />
						</div>
						<div>
							<PickerLabel>To</PickerLabel>
							<ReactHookDatePicker control={control} name="to" />
						</div>
					</PickersRow>
					<ActionsRow>
						{allowClear && (
							<Button variant="outlined" onClick={clear}>
								Clear
							</Button>
						)}
						<Button variant="contained" onClick={applyCustom}>
							Apply
						</Button>
					</ActionsRow>
				</DateRangePickerPopup.Body>
			</DateRangePickerPopup>
		</Box>
	);
};

const RangeLabel = styled.span`
	font-size: 0.875rem;
	color: rgba(0, 0, 0, 0.55);
	text-transform: capitalize;
	white-space: nowrap;
`;

const PickersRow = styled.div`
	display: flex;
	gap: 1rem;
	justify-content: center;
	margin-top: 1rem;
`;

const PickerLabel = styled.div`
	font-weight: 600;
	margin-bottom: 0.25rem;
	text-align: center;
`;

const ActionsRow = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 1rem;
	margin-top: 0.5rem;
`;

export default HubDateRange;
